"use client";

import React from "react";
import { Clock, MonitorPlay, Users, ArrowLeft } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";

declare const wvcClient: any;

function SingleCorsoConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 max-w-4xl">
        <Card className="overflow-hidden border-none shadow-lg animate-pulse bg-card">
          <CardHeader className="pb-6 pt-8 md:pt-12 md:px-10">
            <div className="h-12 bg-muted rounded-md w-3/4 mb-4"></div>
          </CardHeader>
          <div className="w-full px-6 md:px-10 pb-6">
            <div className="w-full h-[400px] bg-muted rounded-xl"></div>
          </div>
          <CardContent className="md:px-10 pb-12">
            <div className="h-24 bg-muted rounded-xl mb-8"></div>
            <div className="space-y-4">
              <div className="h-4 bg-muted rounded w-full"></div>
              <div className="h-4 bg-muted rounded w-5/6"></div>
              <div className="h-4 bg-muted rounded w-4/6"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 max-w-4xl text-center py-12">
        <p className="text-destructive font-medium">Errore nel caricamento del corso.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 max-w-4xl text-center py-12">
        <p className="text-muted-foreground font-medium">Nessun corso trovato.</p>
      </div>
    );
  }

  const post = posts[0];
  const cf = post.customFields || {};

  return (
    <div className="container mx-auto px-4 max-w-4xl">
      <Card className="overflow-hidden border-border/50 shadow-xl bg-card">
        <CardHeader className="pb-6 pt-8 md:pt-12 md:px-10">
          <CardTitle asChild>
            <h1 className="text-3xl md:text-5xl font-default font-bold text-foreground leading-tight">
              {post.title}
            </h1>
          </CardTitle>
        </CardHeader>

        {post.featuredImage && (
          <div className="w-full px-6 md:px-10 pb-6">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="w-full h-auto max-h-[500px] object-cover rounded-xl shadow-sm"
              loading="lazy"
            />
          </div>
        )}

        <CardContent className="md:px-10 pb-12">
          {(cf.duration || cf.recipients || cf.delivery_method) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10 p-6 bg-muted/30 rounded-xl border border-border/50">
              {cf.duration && (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 text-primary">
                    <Clock className="w-4 h-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Durata</span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{cf.duration}</p>
                </div>
              )}
              {cf.recipients && (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 text-primary">
                    <Users className="w-4 h-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Destinatari</span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{cf.recipients}</p>
                </div>
              )}
              {cf.delivery_method && (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2 text-primary">
                    <MonitorPlay className="w-4 h-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Modalità</span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{cf.delivery_method}</p>
                </div>
              )}
            </div>
          )}

          <div
            className="prose prose-slate dark:prose-invert max-w-none font-sans text-muted-foreground leading-relaxed [&>h2]:font-default [&>h2]:text-foreground [&>h2]:font-semibold [&>h3]:font-default [&>h3]:text-foreground [&>h3]:font-semibold [&>strong]:text-foreground"
            dangerouslySetInnerHTML={{ __html: post.content || cf.content || "" }}
          />
        </CardContent>
      </Card>

      <div className="mt-8 flex justify-center">
        <Link to="/TrainingPage">
          <Button variant="outline" className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Torna alla Formazione
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function SingleCorsoTemplateSection() {
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-corso-template-section" className="bg-background py-12 md:py-24">
      <PostsProvider wp_query={wp_query}>
        <SingleCorsoConsumer />
      </PostsProvider>
    </section>
  );
}